import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { supabase } from "../lib/supabaseClient";
import type { SermonRow } from "../lib/supabaseClient";

export interface SermonData {
  id: number;
  category: string;
  categoryName: string;
  title: string;
  scripture: string[]; // 본문 말씀 배열
  date: string;
  preacher: string;
  content?: string;
  youtubeUrl?: string;
  view_count?: number;
}

interface SermonContextType {
  sermons: SermonData[];
  loading: boolean;
  error: string | null;
  getSermon: (id: number) => SermonData | undefined;
  addSermon: (sermon: Omit<SermonData, "id">) => Promise<void>;
  updateSermon: (
    id: number,
    updatedSermon: Partial<SermonData>,
  ) => Promise<void>;
  deleteSermon: (id: number) => Promise<void>;
  refreshSermons: () => Promise<void>;
  incrementLocalViewCount: (id: number) => void;
}

const mapToSermonData = (row: SermonRow): SermonData => ({
  id: row.id,
  category: row.category,
  categoryName: row.category_name,
  title: row.title,
  scripture: row.scripture,
  date: row.date,
  preacher: row.preacher,
  content: row.content || undefined,
  youtubeUrl: row.youtube_url || undefined,
  view_count: row.view_count,
});

export const SermonContext = createContext<SermonContextType | undefined>(
  undefined,
);

export const SermonProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [sermons, setSermons] = useState<SermonData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSermons = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("sermons")
        .select("*")
        .order("date", { ascending: false });

      if (error) throw error;
      setSermons((data || []).map(mapToSermonData));
      setError(null);
    } catch (err: any) {
      setError(err.message);
      console.error("Error fetching sermons:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSermons();
  }, []);

  const getSermon = (id: number) => {
    return sermons.find((sermon) => sermon.id === id);
  };

  const addSermon = async (newSermon: Omit<SermonData, "id">) => {
    const { error } = await supabase
      .from("sermons")
      .insert([
        {
          category: newSermon.category,
          category_name: newSermon.categoryName,
          title: newSermon.title,
          scripture: newSermon.scripture, // PostgreSQL text[] 배열
          date: newSermon.date,
          preacher: newSermon.preacher,
          content: newSermon.content || null,
          youtube_url: newSermon.youtubeUrl || null,
        },
      ])
      .select();

    if (error) throw error;
    await fetchSermons();
  };

  const updateSermon = async (
    id: number,
    updatedSermon: Partial<SermonData>,
  ) => {
    const updateData: any = {};
    if (updatedSermon.category) updateData.category = updatedSermon.category;
    if (updatedSermon.categoryName)
      updateData.category_name = updatedSermon.categoryName;
    if (updatedSermon.title) updateData.title = updatedSermon.title;
    if (updatedSermon.scripture !== undefined)
      updateData.scripture = updatedSermon.scripture; // PostgreSQL text[] 배열
    if (updatedSermon.date) updateData.date = updatedSermon.date;
    if (updatedSermon.preacher) updateData.preacher = updatedSermon.preacher;
    if (updatedSermon.content !== undefined)
      updateData.content = updatedSermon.content;
    if (updatedSermon.youtubeUrl !== undefined)
      updateData.youtube_url = updatedSermon.youtubeUrl;

    const { error } = await supabase
      .from("sermons")
      .update(updateData)
      .eq("id", id);

    if (error) throw error;
    await fetchSermons();
  };

  const deleteSermon = async (id: number) => {
    const { error } = await supabase.from("sermons").delete().eq("id", id);

    if (error) throw error;
    await fetchSermons();
  };

  const incrementLocalViewCount = useCallback((id: number) => {
    setSermons((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, view_count: (s.view_count || 0) + 1 } : s,
      ),
    );
  }, []);

  return (
    <SermonContext.Provider
      value={{
        sermons,
        loading,
        error,
        getSermon,
        addSermon,
        updateSermon,
        deleteSermon,
        refreshSermons: fetchSermons,
        incrementLocalViewCount,
      }}
    >
      {children}
    </SermonContext.Provider>
  );
};
